import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import WhatsNewDialog from './WhatsNewDialog';

export default function Header({ onMenuClick, version }) {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [showWhatsNew, setShowWhatsNew] = useState(false);

  // Toggle theme and update document class
  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
    if (!isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  };

  // Check for saved theme or system preference on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;

    if (savedTheme === 'dark' || (!savedTheme && prefersDark)) {
      setIsDarkMode(true);
      document.documentElement.classList.add('dark');
    } else {
      setIsDarkMode(false);
      document.documentElement.classList.remove('dark');
    }
  }, []);

  return (
    <>
      <header className="w-full sticky top-0 z-99 bg-color-white shadow px-3 md:px-4 py-2 md:py-4 text-xl font-bold border-b border-color-gray-200 flex items-center justify-between dark:bg-color-gray-800 dark:border-color-gray-700 dark:text-color-white">
        <div className='flex items-center sm:space-x-4 space-x-2'>
          {/* Hamburger for mobile */}
          <button
            className="lg:hidden p-[5px] rounded dark:text-color-white text-color-gray-800 bg-color-gray-100 dark:bg-color-gray-700 focus:outline-none"
            onClick={onMenuClick}
            aria-label="Open sidebar"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <Link to="/">
            <svg className='h-7 md:h-9' version="1.1" viewBox="0 0 293.8 73.9">
              <g>
                <g>
                  <path className="fill-color-blue-950 dark:fill-color-white" d="M193.2,27.3c1.5-2.7,3.5-4.8,6-6.3s5.5-2.2,8.8-2.2,7.9,1.1,10.7,3.2c2.8,2.1,4.6,5,5.4,8.7h-4.8c-.6-2.5-1.9-4.5-3.9-5.9-2-1.4-4.5-2.1-7.4-2.1s-4.5.5-6.3,1.6c-1.9,1.1-3.4,2.7-4.5,4.8-1.1,2.1-1.6,4.7-1.6,7.8s.5,5.7,1.6,7.8c1.1,2.1,2.6,3.7,4.5,4.8,1.9,1.1,4,1.6,6.3,1.6s5.4-.7,7.4-2.1c2-1.4,3.3-3.4,3.9-6h4.8c-.8,3.6-2.6,6.5-5.4,8.6-2.8,2.2-6.4,3.3-10.6,3.3s-6.2-.7-8.8-2.2c-2.6-1.5-4.6-3.6-6-6.3-1.5-2.7-2.2-5.9-2.2-9.5s.7-6.8,2.2-9.5Z" />
                  <path className="fill-color-blue-950 dark:fill-color-white" d="M234.5,52c-2.5-1.9-4-4.4-4.3-7.6h4.6c.2,2,1.1,3.6,2.8,4.8,1.6,1.2,3.9,1.8,6.6,1.8s4.3-.6,5.7-1.7c1.4-1.2,2.1-2.6,2.1-4.3s-.4-2.2-1.2-2.9-1.7-1.4-2.9-1.8c-1.2-.4-2.8-.9-4.8-1.4-2.6-.7-4.7-1.4-6.3-2-1.6-.7-3-1.7-4.1-3-1.1-1.3-1.7-3.1-1.7-5.4s.5-3.3,1.5-4.7c1-1.5,2.5-2.6,4.4-3.5,1.9-.9,4-1.3,6.4-1.3,3.8,0,6.8,1,9.1,2.9,2.3,1.9,3.5,4.5,3.7,7.8h-4.5c-.1-2-.9-3.7-2.4-5-1.5-1.3-3.5-1.9-6.1-1.9s-4.1.5-5.5,1.6c-1.4,1.1-2.1,2.4-2.1,4s.4,2.5,1.2,3.4c.8.9,1.9,1.6,3.1,2,1.2.5,2.9,1,5,1.6,2.5.7,4.5,1.3,6,2,1.5.6,2.8,1.6,3.8,2.8,1.1,1.2,1.6,2.9,1.7,4.9,0,1.9-.5,3.6-1.5,5-1,1.5-2.5,2.6-4.3,3.5-1.8.9-4,1.3-6.3,1.3-4,0-7.3-.9-9.8-2.8Z" />
                  <path className="fill-color-blue-950 dark:fill-color-white" d="M267.2,52c-2.5-1.9-4-4.4-4.3-7.6h4.6c.2,2,1.1,3.6,2.8,4.8,1.6,1.2,3.9,1.8,6.6,1.8s4.3-.6,5.7-1.7c1.4-1.2,2.1-2.6,2.1-4.3s-.4-2.2-1.2-2.9-1.7-1.4-2.9-1.8c-1.2-.4-2.8-.9-4.8-1.4-2.6-.7-4.7-1.4-6.3-2-1.6-.7-3-1.7-4.1-3-1.1-1.3-1.7-3.1-1.7-5.4s.5-3.3,1.5-4.7c1-1.5,2.5-2.6,4.4-3.5,1.9-.9,4-1.3,6.4-1.3,3.8,0,6.8,1,9.1,2.9,2.3,1.9,3.5,4.5,3.7,7.8h-4.5c-.1-2-.9-3.7-2.4-5-1.5-1.3-3.5-1.9-6.1-1.9s-4.1.5-5.5,1.6c-1.4,1.1-2.1,2.4-2.1,4s.4,2.5,1.2,3.4c.8.9,1.9,1.6,3.1,2,1.2.5,2.9,1,5,1.6,2.5.7,4.5,1.3,6,2,1.5.6,2.8,1.6,3.8,2.8,1.1,1.2,1.6,2.9,1.7,4.9,0,1.9-.5,3.6-1.5,5-1,1.5-2.5,2.6-4.3,3.5-1.8.9-4,1.3-6.3,1.3-4,0-7.3-.9-9.8-2.8Z" />
                </g>
                <path className="fill-color-blue-950 dark:fill-color-white" d="M108.2,54.8c-3.2,0-6.2-.6-8.7-1.9-2.5-1.2-4.5-3.1-6-5.7-1.5-2.5-2.2-6-2.2-9.6v-18.8h8.6v18.9c0,2.8.7,5.4,2.2,7,1.5,1.6,3.6,2.4,6.4,2.4s4.9-.8,6.4-2.4c1.5-1.6,2.2-4.1,2.2-7v-18.9h8.6v18.8c0,3.7-.8,7.1-2.3,9.6-1.5,2.5-3.6,4.4-6.2,5.7-2.6,1.3-5.6,1.9-8.9,1.9Z" />
                <path className="fill-color-blue-950 dark:fill-color-white" d="M158.3,54.8v-19.2c0-2.8-.7-5.1-2.2-6.7-1.5-1.6-3.6-2.4-6.4-2.4s-4.9.8-6.4,2.4c-1.5,1.6-2.2,3.8-2.2,6.7v19.2h-8.6v-19.1c0-3.7.8-6.8,2.3-9.3,1.5-2.5,3.6-4.4,6.2-5.7,2.6-1.3,5.6-1.9,8.9-1.9s6.2.6,8.7,1.9c2.5,1.2,4.5,3.1,6,5.7,1.5,2.5,2.2,5.7,2.2,9.3v19.1h-8.6Z" />
                <rect className="fill-color-blue-950 dark:fill-color-white" x="173.6" y="18.8" width="8.6" height="7.5" />
                <rect className="fill-color-blue-950 dark:fill-color-white" x="173.6" y="31.4" width="8.6" height="23.4" />
              </g>
              <g>
                <path className="fill-color-violet-700" d="M35.2,66.7c0-2.2-1.8-3.9-3.9-3.9-4.4,0-13,0-13.3,0-3.1,0-4.7-1.1-4.7-3.4v-17.5c0-1.3-.5-2.5-1.6-3.6-1.1-.9-2.2-1.8-3.3-2.6,1.1-.9,2.2-1.8,3.3-2.6,1.1-1.1,1.6-2.3,1.6-3.6v-15.3c0-2.3,1.6-3.4,4.7-3.4s1,0,1.7,0c.5,0,10.7,0,15.5,0h0c0-2.2-1.8-4-4-4h-15.7c-2,0-3.6.6-4.9,1.9-1.3,1.3-1.9,2.9-1.9,4.9v16.5l-5.6,5.1v1l5.6,5.2v18.6c0,2,.6,3.6,1.9,4.9,1.3,1.3,2.9,1.9,4.9,1.9h19.7Z" />
                <path className="fill-color-violet-700" d="M64.7,30.1V13.6c0-1.9-.6-3.6-1.9-4.8-1.3-1.3-2.9-1.9-4.8-1.9h-19.7c0,2.2,1.8,4,4,4,4.4,0,11.1,0,11.5,0,.7,0,1.3,0,1.7,0,3.1,0,4.7,1.1,4.7,3.4v15.3c0,1.4.5,2.6,1.6,3.6,1.1.9,2.2,1.7,3.3,2.6-1.1.9-2.2,1.7-3.3,2.6-1.1,1.1-1.6,2.3-1.6,3.6v17.5c0,2.3-1.6,3.4-4.7,3.4s-1,0-1.7,0c-.4,0-9.7,0-15.5,0h0c0,2.2,1.8,4,4,4h15.7c1.9,0,3.6-.6,4.8-1.9,1.3-1.3,1.9-2.9,1.9-4.8v-18.6l5.6-5.2v-1l-5.6-5.1Z" />
              </g>
              <path className="fill-color-pink-600" d="M36.7,50.5c-8.1,0-14.7-6.6-14.7-14.7v-12.7h8v12.7c0,3.7,3,6.7,6.7,6.7s6.7-3,6.7-6.7v-12.7h8v12.7c0,8.1-6.6,14.7-14.7,14.7Z" />
            </svg>
          </Link>
          <p className='text-xs font-semibold font-mono text-color-gray-600 dark:text-color-gray-300 bg-color-gray-100 dark:bg-color-gray-700 rounded-md px-2 py-1 leading-none'>{version}</p>
        </div>

        <div className="flex items-center space-x-3">
          {/* What's New Button */}
          <button
            onClick={() => setShowWhatsNew(true)}
            className="flex cursor-pointer items-center sm:space-x-2 sm:px-3 px-2.5 py-2.5 text-sm font-medium text-color-blue-600 dark:text-color-blue-400 hover:text-color-blue-700 dark:hover:text-color-blue-300 bg-color-blue-50 dark:bg-color-blue-900/30 hover:bg-color-blue-100 dark:hover:bg-color-blue-900/50 rounded-full transition-color"
            aria-label="What's new"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <span className="hidden sm:inline">What's New</span>
          </button>


          <a
            href="https://www.npmjs.com/package/@unizap/unicss"
            target="_blank"
            rel="noopener noreferrer"
            className="size-10 cursor-pointer p-2 rounded-full bg-color-gray-200 dark:bg-color-gray-700 text-color-gray-800 dark:text-color-gray-100 hover:bg-color-gray-300 dark:hover:bg-color-gray-600 transition-color"
            title="View on npm"
          >
            {/* NPM Icon */}
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M20.001 3C20.5533 3 21.001 3.44772 21.001 4V20C21.001 20.5523 20.5533 21 20.001 21H4.00098C3.44869 21 3.00098 20.5523 3.00098 20V4C3.00098 3.44772 3.44869 3 4.00098 3H20.001ZM17.001 7H7.00098V17H12.001V9.5H14.501V17H17.001V7Z"></path></svg>
          </a>
          {/* Theme Toggle Button */}
          <button
            onClick={toggleTheme}
            className="cursor-pointer p-2 rounded-full bg-color-gray-200 dark:bg-color-gray-700 text-color-gray-800 dark:text-color-gray-100 hover:bg-color-gray-300 dark:hover:bg-color-gray-600 transition-color"
            aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDarkMode ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            )}
          </button>
        </div>
      </header>

      {/* What's New Dialog */}
      <WhatsNewDialog
        isOpen={showWhatsNew}
        onClose={() => setShowWhatsNew(false)}
      />
    </>
  );
}